import { preloadContentHeader } from '@confluence/content-header/entry-points/preloadContentHeader.ts';
// @ts-ignore-next-line Ignore error that preloadContentTypesHeader has no types, it's a JS file
import { preloadContentTypesHeader } from '@confluence/content-types-header/entry-points/preloadContentTypesHeader.js';
import { preloadCustomHeaderAndFooter } from '@confluence/custom-header-footer/entry-points/preloadCustomHeaderAndFooter.ts';
import { preloadPageTitleContentProperties } from '@confluence/content-topper/entry-points/preloadPageTitleContentProperties.ts';
import { preloadMediaToken } from '@confluence/fabric-media-support/entry-points/preloadMediaToken.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { preloadFocusedInlineComment } from '@confluence/inline-comments-queries/entry-points/preloadFocusedInlineComment.ts';
import { preloadEditorData } from '@confluence/load-edit-page/entry-points/preloadEditorData.ts';
import { preloadRecordLoomButton } from '@confluence/loom-utils/entry-points/preloadRecordLoomButton.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { prepareGuardExceptionTask } from '@confluence/query-preloader-tools/entry-points/prepareGuardExceptionTask.ts';
import { preloadPageReactions } from '@confluence/reactions/entry-points/preloadPageReactions.ts';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { preloadContentPrerequisites } from '@confluence/content-prerequisites/entry-points/preloadContentPrerequisites.ts';
import { preloadSpaceDetail } from '@confluence/space-utils/entry-points/index.ts';
import { preloadPageStatusQuery } from '@confluence/query-preloaders-common/entry-points/preloadPageStatusQuery.ts';
import { preloadLandUsersInEditorWithRevampedBpqa } from '@confluence/onboarding-helpers/entry-points/preloadLandUsersInEditorWithRevampedBpqa.ts';
import { preloadLegacyBridge } from '@confluence/query-preloaders-common/entry-points/preloadLegacyBridge.ts';
import { preloadEndOfPageRecommendation } from '@confluence/page-recommendations/entry-points/preloadEndOfPageRecommendation.ts';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';
import {
	ATL_PAGE_CONTENT_FOOTER_ACTIONS,
	ATL_GENERAL,
	ATL_FOOTER,
} from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { preloadWebPanelLocation } from '@confluence/web-panel-location/entry-points/preloadWebPanelLocation.ts';
import { preloadAIFloatingContextMenu } from '@confluence/ai-floating-context-menu/entry-points/preloadAIFloatingContextMenu.ts';
import { preloadShareAndRestrictButtonQuery } from '@confluence/share-and-restrict-dialog/entry-points/preloadShareAndRestrictButtonQuery.ts';
import { preloadLivePageData } from '@confluence/load-live-page/entry-points/preloadLivePageData.ts';
import { preloadUseIsAIEnabledForContentQuery } from '@confluence/ai-common/entry-points/preloadUseIsAIEnabledForContentQuery.ts';
import { preloadUseExtensionList } from '@confluence/forge-ui/entry-points/preloadUseExtensionList.ts';
import { FORGE_MODULE_XEN_MACRO } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { preloadFooterComments as legacyPreloadFooterComments } from '@confluence/comments-panel-queries/entry-points/preloadFooterComments.ts';
import { preloadObjectComments } from '@confluence/object-comments/entry-points/preloadObjectComments.ts';
import { preloadAddCommentPermissionCheck } from '@confluence/comments-panel-queries/entry-points/preloadAddCommentPermissionCheck.ts';
import {
	getIsEmbeddedConfluence,
	getUsername,
	hasEmbeddedAllowedFeatures,
} from '@confluence/query-preloaders-common/entry-points/matchHelpers.ts';
import { matchBlogPage } from '@confluence/query-preloaders-common/entry-points/matchRoutes.ts';
import { preloadNavigationTasks } from '@confluence/query-preloaders-common/entry-points/preloadNavigationTasks.ts';
import { preloadSmartCreateFeedbackBanner } from '@confluence/editor-action-bar/entry-points/preloadSmartCreateFeedbackBanner.ts';
import { preloadSiteUserTraits } from '@confluence/targeting/entry-points/preloadSiteUserTraits.ts';
import { preloadAllOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadAllOnboardingState.ts';

/*
 * preloadLivePageRoute preload queries are currently arranged in order of latency, with slowest
 * queries pushed to the tasks array first.
 */
export const preloadLivePageRoute = async (match: RouteMatch, url: string) => {
	const {
		isLicensed,
		userId,
		edition,
		cloudId,
		activationId,
		accessStatus,
		isLoggedIn,
		isAdminHubAIEnabled,
		isRovoEnabled,
		isRovoLLMEnabled,
		environment,
	} = getPreloaderFnContext();
	const tasks: Promise<any>[] = [];
	const { spaceKey, contentId } = match.params;
	const username = getUsername(match);
	const isEmbeddedConfluence = getIsEmbeddedConfluence(match);
	const isBlog = Boolean(matchBlogPage(url));
	const shouldPreloadNavigation = !isEmbeddedConfluence;

	if (expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true)) {
		// replaces preloadEditorData
		tasks.push(
			preloadLivePageData({
				userId,
				contentId,
				spaceKey,
				cloudId,
			}),
		);
	} else {
		tasks.push(
			prepareGuardExceptionTask('EditorData', () =>
				preloadEditorData({
					spaceKey,
					contentId,
					shouldPreloadMentionsProvider: true,
					useCache: false,
					cloudId,
					isBlog,
					isLivePage: true,
				}),
			),
		);
	}

	// 1. Byline and possibly content prerequisites
	tasks.push(
		preloadContentHeader({
			spaceKey,
			contentId,
			isLicensed,
			userId,
			username,
			edition,
			isPrevEditRoute: false,
			useNewContentTopper: true,
			isLivePageRoute: true,
		}).then((resultOrHasErrorBoolean) => {
			if (typeof resultOrHasErrorBoolean === 'boolean') {
				if (resultOrHasErrorBoolean) {
					return preloadContentPrerequisites(contentId, spaceKey);
				}
			} else {
				const { results, hasErrors } = resultOrHasErrorBoolean;
				if (!hasErrors) {
					return results;
				}
				return preloadContentPrerequisites(contentId, spaceKey);
			}
		}),
		preloadLegacyBridge({ contentId, isBlog }),
	);
	if (!expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true)) {
		tasks.push(preloadPageStatusQuery(contentId));
	}

	// 2. Page title
	!expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true) &&
		tasks.push(preloadPageTitleContentProperties({ contentId, spaceKey }));

	// 3. Space details
	!expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true) &&
		tasks.push(preloadSpaceDetail(spaceKey));

	// 4. Content types header (object header)
	if (fg('confluence_frontend_content_wrapper')) {
		tasks.push(preloadContentTypesHeader(contentId, fg('confluence_frontend_content_wrapper')));
		// (No need to call preloadPageRestrictionsQuery() here; it will be called by
		//  preloadContentHeader() above)
	}

	// 4a. Unified Share and Restrict Button
	if (
		!expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true) &&
		fg('confluence_frontend_usd_ssr')
	) {
		tasks.push(preloadShareAndRestrictButtonQuery(contentId));
	}

	// 5. Navigation and page tree
	if (shouldPreloadNavigation) {
		tasks.push(preloadNavigationTasks(spaceKey, contentId, isLicensed, isBlog));
	}

	// 5a. Preload site user traits for personalization
	// preloadSiteUserTraits will handle the access status and ensure licensed access only
	if (fg('cc_activation_hide_changeboarding_for_new_users')) {
		tasks.push(preloadSiteUserTraits());
	}

	// 6. Inline Comments
	if (match?.query.focusedCommentId) {
		tasks.push(preloadFocusedInlineComment(match?.query.focusedCommentId as string, contentId));
	}

	// 7. Reactions
	if (!isEmbeddedConfluence || hasEmbeddedAllowedFeatures('page-reactions', match)) {
		// TODO: delete
		// Preload whether the server has enabled reactions at all to show placeholder
		tasks.push(preloadWebPanelLocation({ contentId, location: ATL_PAGE_CONTENT_FOOTER_ACTIONS }));
		// Preload reactions asynchronously so SSR won't wait for it
		// it's best effort preloading so if it preloads then we have the data, otherwise SPA can load it
		tasks.push(
			preloadPageReactions({
				pageId: contentId,
				containerId: spaceKey,
			}),
		);
	}

	// 10. Stuff only needed for initial load
	tasks.push(
		// We only refresh token when it is about to expire.
		preloadMediaToken(contentId),
	);
	!expValEquals('confluence_live_pages_unified_query_v2', 'isEnabled', true) &&
		void preloadCustomHeaderAndFooter(spaceKey);

	// Preload onboarding state for live page
	if (
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS ||
			accessStatus === AccessStatus.EXTERNAL_COLLABORATOR_ACCESS) &&
		expVal('confluence_centralized_onboarding_state', 'isEnabled', false)
	) {
		tasks.push(preloadAllOnboardingState());
	}

	tasks.push(
		preloadWebPanelLocation({ contentId, location: ATL_GENERAL }),
		preloadWebPanelLocation({ contentId, location: ATL_FOOTER }),
	);

	// 11. Object Sidebar Control + AI Button
	if (isAdminHubAIEnabled) {
		tasks.push(preloadAIFloatingContextMenu(contentId, isBlog));
	}

	// 12. Preload Loom button in Action Bar
	if (
		(accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
			accessStatus === AccessStatus.LICENSED_USE_ACCESS) &&
		isLoggedIn
	) {
		tasks.push(
			preloadRecordLoomButton({
				cloudId,
				activationId,
				accessStatus,
				isLoggedIn,
				userId,
				environment,
			}),
		);
	}

	// 13. Preload content-based AI enablement check
	if (fg('confluence_ai_access_policy')) {
		const shouldShowRovo = fg('rovo_use_rovo_llm_enabled_check') ? isRovoLLMEnabled : isRovoEnabled;
		if (isAdminHubAIEnabled || shouldShowRovo) {
			tasks.push(preloadUseIsAIEnabledForContentQuery(contentId));
		}
	}

	// 14. Preload forge extension manifest
	if (fg('confluence_preload_forge_viewport_heights_editor')) {
		tasks.push(
			preloadUseExtensionList({
				contentId,
				spaceKey,
				moduleType: FORGE_MODULE_XEN_MACRO,
			}),
		);
	}

	// 15. Preload footer comments
	if (fg('confluence_frontend_content_wrapper')) {
		// this query is needed for the new redesigned footer comment experience
		if (fg('confluence_frontend_content_wrapper')) {
			// Use new preloadObjectComments when experiment is enabled, otherwise use legacy
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
				tasks.push(preloadObjectComments(contentId));
			} else {
				tasks.push(legacyPreloadFooterComments(contentId));
			}
		}
	}

	// 16. Preload add comment permission check
	if (fg('confluence_frontend_content_wrapper')) {
		if (fg('confluence_frontend_content_wrapper')) {
			tasks.push(preloadAddCommentPermissionCheck(contentId));
		}
	}

	// 17. Recommendations
	const recommendationsTimeout = StatsigConfigurations.getNumber(
		'confluence_frontend_preload_recommendation_timeout',
		SLOW_QUERY_DEFAULT_TIMEOUT,
	);
	tasks.push(
		slowQueryGuard(
			'preloadEndOfPageRecommendation',
			() => {
				return preloadEndOfPageRecommendation({
					contentId,
					spaceKey,
					contentType: isBlog ? 'blogpost' : 'page',
				});
			},
			recommendationsTimeout,
		),
	);

	//18. Preload Smart Create Feedback Banner
	if (fg('aifc_create_enabled')) {
		tasks.push(preloadSmartCreateFeedbackBanner(contentId));
	}

	//19. Add Onboarding State for revamped bpqa
	if (fg('cfind-5063-revamped-bpqa-live-page-preload-gate')) {
		tasks.push(preloadLandUsersInEditorWithRevampedBpqa());
	}

	return waitForAllReturnWhenError(tasks);
};
